import { useEffect, useState, type FormEvent } from 'react';
import { AssessmentProjectBox } from './client/AssessmentsScreen';
import {
  backupStatus,
  changePassphrase,
  checkIntegrity,
  getAutoLockMinutes,
  listSnapshots,
  makeBackupFile,
  restoreFromBackupFile,
  restoreSnapshot,
  setAutoLockMinutes,
  storageInfo,
  verifyPassphrase,
} from '@/services/appSession';
import type { IntegrityReport } from '@/data/integrity';
import { useRequiredSession, useSession } from '../state/session';
import { PassphraseHint, passphraseProblem } from '../components/PassphraseHint';
import { RestoreBackup } from '../components/RestoreBackup';
import { flushAll } from '../state/saves';
import { SyncSettings } from '../components/SyncSettings';
import { CopyButton } from '../components/CopyButton';
import { getPref, setPref } from '@/platform/prefs';
import { isVerbatimNotation, VERBATIM_NOTATIONS, type VerbatimNotation } from '@/domain/workflow/docModel';
import { projectInstructionFull, theoryContextFile } from '@/domain/ai/knowledge';
import { DEFAULT_SUPERVISION_GUIDELINES } from '@/domain/ai/reportWriting';
import type { AppSettings } from '@/domain/schema/v1';
import { useService } from '../hooks/useService';
import { useLoad } from '../hooks/useLoad';
import { useAutosave } from '../hooks/useAutosave';
import { SaveIndicator, TextInput } from '../components/fields';
import { useSyncUi } from '../state/sync';
import type { RemoteFile } from '@/data/sync/remote';
import type { AppSession } from '@/services/appSession';
import { backupToDrive, driveBackupDate, listDriveBackups, restoreFromDriveBackup } from '@/services/driveBackup';

async function loadInfo(session: ReturnType<typeof useRequiredSession>) {
  return {
    minutes: await getAutoLockMinutes(),
    backup: await backupStatus(),
    storage: await storageInfo(),
    snaps: await listSnapshots(session.db),
  };
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ko-KR') : '없음');

function errorText(e: unknown): string {
  const n = (e as Error)?.name;
  if (n === 'WrongSecretError') return 'passphrase가 맞지 않습니다.';
  if (n === 'IntegrityError') return (e as Error).message;
  if (n === 'StorageFullError') return (e as Error).message;
  return '작업에 실패했습니다. 기존 데이터는 그대로입니다.';
}

export function SettingsScreen() {
  const session = useRequiredSession();
  const { setSession, lock } = useSession();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [info, setInfo] = useState<Awaited<ReturnType<typeof loadInfo>> | null>(null);
  const [version, setVersion] = useState(0);
  const [report, setReport] = useState<IntegrityReport | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => setVersion((v) => v + 1);
  const minutes = info?.minutes ?? 10;
  const backup = info?.backup ?? null;
  const storage = info?.storage ?? null;
  const snaps = info?.snaps ?? [];

  useEffect(() => {
    let cancelled = false;
    void loadInfo(session).then((d) => {
      if (!cancelled) setInfo(d);
    });
    return () => {
      cancelled = true;
    };
  }, [session, version]);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ kind: 'ok', text: await fn() });
      refresh();
    } catch (e) {
      setMsg({ kind: 'err', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  const doBackup = () =>
    run(async () => {
      await flushAll();
      const { fileName, blob } = await makeBackupFile(session);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return `백업 파일을 저장했습니다: ${fileName}`;
    });

  const backupCard = (
    <div className="card">
      <h2>백업</h2>
      <p>
        마지막 백업: {fmt(backup?.lastBackupAt ?? null)}
        {backup?.overdue && <span className="warn"> · 백업한 지 7일이 넘었습니다</span>}
      </p>
      <p className="muted small">
        백업은 암호화되어 있어 현재 passphrase로만 열 수 있습니다. Google Drive에 올려 두면 어느 기기에서든 복원할 수
        있고, Drive가 연결되어 있으면 일주일마다 자동으로 하나씩 올라갑니다 (최근 8개 보관).
      </p>
      <DriveBackups busy={busy} run={run} onRestored={setSession} />
      <button type="button" disabled={busy} onClick={() => void doBackup()}>
        이 기기에 백업 파일로 저장
      </button>
      <RestoreBackup
        busy={busy}
        onRestore={(text, pass) =>
          run(async () => {
            setSession(await restoreFromBackupFile(text, pass));
            return '복원했습니다. 복원 전 데이터는 스냅샷으로 보관되어 있습니다.';
          })
        }
      />
    </div>
  );

  return (
    <section className="stack settings">
      <h1 className="page-title">설정</h1>
      {msg && (
        <p role={msg.kind === 'err' ? 'alert' : 'status'} className={msg.kind === 'err' ? 'warn' : 'ok'}>
          {msg.text}
        </p>
      )}

      <p className="group-title">동기화 · 백업</p>
      <SyncSettings />
      {backupCard}

      <p className="group-title">외부 AI · 문서</p>
      <div className="card stack">
        <h2>외부 AI 연결</h2>
        <p className="muted small">
          내담자마다 외부 AI 프로젝트를 하나씩 만듭니다: 새 프로젝트 창에서 이름을 넣고 컨텍스트에 기준표 파일을 추가해
          생성 → 프로젝트 설정 → 메모리 → 프로젝트 지침 칸에 지침 붙여넣기. 지침과 파일은 모든 내담자가 같습니다.
          회기마다 그 프로젝트 안에서 대화를 새로 시작해 회기 자료를 붙여넣고, 결과를 앱에 붙여넣으면 끝입니다. 외부 AI
          설정에서 "대화를 모델 개선에 사용" 같은 항목은 꺼 두기를 권합니다.
        </p>
        <div className="row">
          <CopyButton className="primary" text={projectInstructionFull()} label="외부 AI 프로젝트 지침 복사" />
          <button
            type="button"
            onClick={() =>
              void import('../export/docx').then(({ saveFile }) => {
                const f = theoryContextFile();
                return saveFile(new Blob([f.content], { type: 'text/markdown;charset=utf-8' }), f.name);
              })
            }
          >
            기준표 파일 받기 (컨텍스트용)
          </button>
        </div>
      </div>
      <AssessmentProjectBox />
      <WritingGuidelines />
      <NotationSetting />
      <CounselorSettings />

      <p className="group-title">계정 · 보안</p>
      <details className="card">
        <summary>
          <strong>보안</strong>{' '}
          <span className="muted small">자동 잠금 {minutes}분 · 비밀번호 바꾸기 · 지금 잠그기</span>
        </summary>
        <button type="button" onClick={lock}>
          지금 잠그기
        </button>
        <label>
          자동 잠금
          <select
            value={minutes}
            onChange={(e) =>
              void run(async () => (await setAutoLockMinutes(Number(e.target.value)), '자동 잠금 시간을 바꿨습니다'))
            }
          >
            {[1, 5, 10, 15, 30].map((m) => (
              <option key={m} value={m}>
                {m}분 동안 사용하지 않으면
              </option>
            ))}
          </select>
        </label>
        <ChangePassphrase
          onDone={(text) => setMsg({ kind: 'ok', text })}
          onError={(e) => setMsg({ kind: 'err', text: errorText(e) })}
        />
      </details>

      <p className="group-title">고급</p>
      <details className="card">
        <summary>
          <strong>데이터 점검</strong> <span className="muted small">무결성 점검 · 저장소 보호</span>
        </summary>

        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const r = await checkIntegrity(session);
              setReport(r);
              return r.corrupted.length === 0
                ? `기록 ${r.checked}건 모두 정상입니다.`
                : `손상된 기록 ${r.corrupted.length}건을 찾았습니다.`;
            })
          }
        >
          무결성 점검
        </button>
        {report && report.corrupted.length > 0 && (
          <ul>
            {report.corrupted.map((c) => (
              <li key={c.id}>
                {c.entityType} · rev {c.revision}{' '}
                <button
                  type="button"
                  onClick={() =>
                    void run(async () => {
                      const r = await session.repo.recoverFromHistory(c.entityType as never, c.id);
                      return `이력의 rev ${r.recoveredFromRevision} 내용으로 복구했습니다.`;
                    })
                  }
                >
                  이력에서 복구
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">
          저장소 보호:{' '}
          {storage?.persisted === true
            ? '영구 저장 허용됨'
            : storage?.persisted === false
              ? '브라우저가 공간 부족 시 지울 수 있음 — 홈 화면에 설치를 권장합니다'
              : '확인 불가'}
        </p>
      </details>

      <details className="card">
        <summary>
          <strong>자동 스냅샷</strong> <span className="muted small">{snaps.length}개 · 이 시점으로 되돌리기</span>
        </summary>
        {snaps.length === 0 ? (
          <p className="muted">아직 없습니다.</p>
        ) : (
          <ul>
            {snaps.map((s) => (
              <li key={s.id}>
                {fmt(s.createdAt)} · {reasonLabel(s.reason)}{' '}
                <ConfirmButton
                  label="이 시점으로 되돌리기"
                  confirmLabel="현재 상태를 스냅샷으로 남기고 되돌립니다. 확인"
                  disabled={busy}
                  onConfirm={() =>
                    void run(async () => {
                      await restoreSnapshot(session.db, s.id);
                      lock(); // 되돌린 시점의 키로 다시 열어야 한다
                      return '되돌렸습니다. 다시 잠금 해제하세요.';
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  );
}

const reasonLabel = (r: string) =>
  ({
    daily: '일일',
    'pre-migration': '업그레이드 전',
    'pre-restore': '복원 전',
    'pre-import': '가져오기 전',
    manual: '수동',
  })[r] ?? r;

function ConfirmButton(props: { label: string; confirmLabel: string; disabled?: boolean; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  return armed ? (
    <>
      <button
        type="button"
        className="danger"
        disabled={props.disabled}
        onClick={() => (setArmed(false), props.onConfirm())}
      >
        {props.confirmLabel}
      </button>
      <button type="button" onClick={() => setArmed(false)}>
        취소
      </button>
    </>
  ) : (
    <button type="button" disabled={props.disabled} onClick={() => setArmed(true)}>
      {props.label}
    </button>
  );
}

function ChangePassphrase({ onDone, onError }: { onDone: (t: string) => void; onError: (e: unknown) => void }) {
  const session = useRequiredSession();
  const setSession = useSession((s) => s.setSession);
  const [open, setOpen] = useState(false);
  const [oldP, setOldP] = useState('');
  const [newP, setNewP] = useState('');
  const [newP2, setNewP2] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = passphraseProblem(newP) ?? (newP !== newP2 ? '새 passphrase 두 입력이 다릅니다.' : null);
    if (problem) return onError(Object.assign(new Error(problem), { name: 'IntegrityError' }));
    try {
      const next = await changePassphrase(session, oldP, newP);
      await verifyPassphrase(newP); // 새 문구로 열리는지 즉시 확인
      setSession(next);
      setOpen(false);
      setOldP('');
      setNewP('');
      setNewP2('');
      onDone('passphrase를 바꿨습니다. 이전 백업 파일은 이전 passphrase로 열립니다.');
    } catch (err) {
      onError(err);
    }
  }

  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)}>
        passphrase 변경
      </button>
    );
  return (
    <form onSubmit={submit} className="stack">
      <label>
        현재 passphrase
        <input type="password" value={oldP} onChange={(e) => setOldP(e.target.value)} autoComplete="current-password" />
      </label>
      <label>
        새 passphrase
        <input type="password" value={newP} onChange={(e) => setNewP(e.target.value)} autoComplete="new-password" />
      </label>
      <PassphraseHint value={newP} />
      <label>
        새 passphrase 다시 입력
        <input type="password" value={newP2} onChange={(e) => setNewP2(e.target.value)} autoComplete="new-password" />
      </label>
      <div className="row">
        <button type="submit" className="primary">
          변경
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          취소
        </button>
      </div>
    </form>
  );
}

/** 축어록 표기 (이 기기): 문서에는 상/내로 저장되고 보기·복사·인쇄·Word에서만 바뀐다 */
function NotationSetting() {
  return (
    <div className="card stack">
      <h2>축어록 표기</h2>
      <NotationSelect label="사례보고서·축어록 칸" prefKey="doc.verbatimNotation" fallback="ko" />
      <NotationSelect label="대화록(Verbatim) 문서" prefKey="doc.dialogueNotation" fallback="pcsub" />
      <p className="muted small">
        문서 보기·복사·인쇄·Word 저장에 적용됩니다. 저장된 문서 내용은 바뀌지 않아 언제든 다시 바꿀 수 있습니다. 이
        기기에만 적용되니 다른 기기에서도 같은 표기를 쓰려면 그 기기에서도 골라 주세요.
      </p>
    </div>
  );
}

function NotationSelect({
  label,
  prefKey,
  fallback,
}: {
  label: string;
  prefKey: 'doc.verbatimNotation' | 'doc.dialogueNotation';
  fallback: VerbatimNotation;
}) {
  const saved = getPref(prefKey);
  const [value, setValue] = useState<VerbatimNotation>(isVerbatimNotation(saved) ? saved : fallback);
  return (
    <label>
      {label}
      <select
        aria-label={`${label} 표기`}
        value={value}
        onChange={(e) => {
          const v = e.target.value as VerbatimNotation;
          setValue(v);
          setPref(prefKey, v);
        }}
      >
        {(Object.keys(VERBATIM_NOTATIONS) as VerbatimNotation[]).map((k) => (
          <option key={k} value={k}>
            {VERBATIM_NOTATIONS[k].name}
            {k === fallback ? ' (기본)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/** 상담자 정보: 문서의 상담자·발표자 칸과 기본 상담 장소 (모든 기기 공통, 동기화됨) */
function CounselorSettings() {
  const svc = useService();
  const { data } = useLoad(() => svc.ensureSettings(), [svc]);
  if (!data) return null;
  return <CounselorForm settings={data} />;
}

function CounselorForm({ settings }: { settings: AppSettings }) {
  const svc = useService();
  const a = useAutosave<AppSettings>(settings, (patch, rev) => svc.updateSettings(patch, rev));
  const v = a.value!;
  return (
    <div className="card stack">
      <div className="card-head">
        <h2>상담자 정보</h2>
        <SaveIndicator status={a.status} error={a.error} />
      </div>
      <TextInput
        label="상담자 이름"
        value={v.counselorName}
        onChange={(x) => a.set('counselorName', x.slice(0, 40) || undefined)}
      />
      <TextInput
        label="소속 기관"
        value={v.organization}
        onChange={(x) => a.set('organization', x.slice(0, 80) || undefined)}
      />
      <TextInput
        label="기본 상담 장소"
        value={v.defaultLocation}
        onChange={(x) => a.set('defaultLocation', x.slice(0, 100) || undefined)}
      />
      <p className="muted small">
        상담기록지·사례보고서(발표자)·종결보고서의 상담자 칸과 회기 장소에 자동으로 들어갑니다.
      </p>
    </div>
  );
}

/** Drive 백업: 올리기 · 목록 · 복원 */
function DriveBackups({
  busy,
  run,
  onRestored,
}: {
  busy: boolean;
  run: (fn: () => Promise<string>) => Promise<void>;
  onRestored: (s: AppSession) => void;
}) {
  const session = useRequiredSession();
  const connected = useSyncUi((x) => x.connected);
  const [list, setList] = useState<RemoteFile[] | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [pass, setPass] = useState('');
  const refresh = () =>
    void listDriveBackups()
      .then(setList)
      .catch(() => setList([]));
  useEffect(() => {
    if (connected) refresh();
  }, [connected]);
  if (!connected)
    return <p className="small">Google Drive를 연결하면 백업을 Drive에 올릴 수 있습니다 (위 동기화 카드에서 연결).</p>;
  return (
    <div className="stack">
      <button
        type="button"
        className="primary"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await flushAll();
            const name = await backupToDrive(session);
            refresh();
            return `Drive에 백업했습니다 (${driveBackupDate(name)})`;
          })
        }
      >
        Drive에 백업
      </button>
      {list && list.length > 0 && (
        <details>
          <summary className="small">Drive에 있는 백업 {list.length}개 · 복원</summary>
          <ul className="list">
            {list.map((f) => (
              <li key={f.name} className="list-row">
                <span className="small">
                  {driveBackupDate(f.name)}
                  {f.size ? <span className="muted"> · {Math.max(1, Math.round(f.size / 1024))}KB</span> : null}
                </span>
                <button type="button" disabled={busy} onClick={() => setPick(f.name)}>
                  이 백업으로 복원
                </button>
              </li>
            ))}
          </ul>
          {pick && (
            <div className="stack">
              <p className="warn small">
                {driveBackupDate(pick)} 백업으로 되돌립니다. 지금 데이터는 스냅샷으로 보관된 뒤 교체됩니다.
              </p>
              <label>
                백업할 때의 passphrase
                <input
                  type="password"
                  autoComplete="current-password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                />
              </label>
              <div className="row">
                <button
                  type="button"
                  className="danger"
                  disabled={busy || !pass}
                  onClick={() =>
                    void run(async () => {
                      onRestored(await restoreFromDriveBackup(pick, pass));
                      setPick(null);
                      setPass('');
                      return '복원했습니다. 복원 전 데이터는 스냅샷으로 보관되어 있습니다.';
                    })
                  }
                >
                  복원
                </button>
                <button type="button" onClick={() => setPick(null)}>
                  취소
                </button>
              </div>
            </div>
          )}
        </details>
      )}
    </div>
  );
}

/** 지도감독 원칙·작성 기준 (A30): 보고서 요청에 매번 함께 보낸다. 새 피드백을 받으면 여기에 덧붙인다 */
function WritingGuidelines() {
  const svc = useService();
  const { data, reload } = useLoad(() => svc.ensureSettings(), [svc]);
  const [text, setText] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  if (!data) return null;
  const current = data.writingGuidelines?.trim() || DEFAULT_SUPERVISION_GUIDELINES;
  const value = text ?? current;
  const save = (v: string | undefined) =>
    void svc
      .updateSettings({ writingGuidelines: v })
      .then(() => {
        setText(null);
        setMsg('저장했습니다. 다음 보고서 요청부터 적용됩니다.');
        reload();
      })
      .catch((e: Error) => setMsg(e.message));
  return (
    <details className="card">
      <summary>
        <strong>지도감독 원칙 · 작성 기준</strong>{' '}
        <span className="muted small">사례보고서·종결보고서를 외부 AI에 맡길 때 매번 함께 보냅니다</span>
      </summary>
      <p className="muted small">
        수퍼비전에서 새 피드백을 받으면 여기에 한 줄 덧붙이세요. 칸별 세부 기준(통합 가설, 사례개념화 서술, 상담전략,
        회기 요약 등)은 앱에 들어 있고, 여기 원칙이 그보다 우선합니다.
      </p>
      <textarea aria-label="지도감독 원칙" rows={16} value={value} onChange={(e) => setText(e.target.value)} />
      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={text === null || text.trim() === current}
          onClick={() => save(value.trim())}
        >
          저장
        </button>
        <button type="button" disabled={!data.writingGuidelines} onClick={() => save(undefined)}>
          기본값으로 되돌리기
        </button>
        {msg && <span className="muted small">{msg}</span>}
      </div>
    </details>
  );
}
